import { notificationsApi } from '../api/notifications/notifications';

export function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const buffer = new ArrayBuffer(rawData.length);
  const outputArray = new Uint8Array(buffer);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Returns true if the browser supports push notifications
 * AND the user has not permanently denied permission.
 */
export function canRequestPush(): boolean {
  if (typeof navigator === 'undefined') return false;
  if (!('serviceWorker' in navigator)) return false;
  if (!('PushManager' in window)) return false;
  if (typeof Notification === 'undefined') return false;
  if (Notification.permission === 'denied') return false;
  return true;
}

/**
 * Checks whether there is an active push subscription on the current
 * service worker registration.
 */
export async function isPushActive(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) return false;
  try {
    const registration = await navigator.serviceWorker.getRegistration('/sw.js');
    if (!registration) return false;
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch {
    return false;
  }
}

/**
 * Requests notification permission, subscribes to push via the service worker,
 * and posts the subscription token to the backend.
 *
 * Only call from an explicit user action (button click) — never auto-prompt.
 * Returns `true` on success.
 */
export async function subscribeToPush(): Promise<boolean> {
  const vapidKey = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;
  if (!vapidKey) return false;
  if (!canRequestPush()) return false;

  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return false;

    const registration = await navigator.serviceWorker.register('/sw.js');
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey),
    });

    const json = subscription.toJSON();
    if (!json.endpoint || !json.keys) return false;

    await notificationsApi.registerPushToken({
      subscription: {
        endpoint: json.endpoint,
        keys: {
          p256dh: json.keys.p256dh!,
          auth: json.keys.auth!,
        },
        expirationTime: (json.expirationTime as number | null) ?? null,
      },
      platform: 'web',
    });

    return true;
  } catch (err) {
    console.error('[push-utils] subscribeToPush failed:', err);
    return false;
  }
}

/**
 * Unsubscribes from push notifications by removing the browser push subscription.
 * The backend will naturally ignore stale endpoints.
 */
export async function unsubscribePush(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  try {
    const registration = await navigator.serviceWorker.getRegistration('/sw.js');
    if (!registration) return;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      await subscription.unsubscribe();
    }
  } catch (err) {
    console.error('[push-utils] unsubscribePush failed:', err);
  }
}
